/**
 * Форма одного рабочего места СОУТ (страница «Рабочее место — <должность>», canon
 * service-sout, p3-sout-workplaces §5–§13, Волна 6). Восемь блоков-карточек по канону
 * `#s-sout-order-wp` макета (`docs/changes-1/Центрика_—_кабинет_КЛИЕНТА_заказ обучения.html`,
 * ~798–860) и тексту ТЗ `docs/changes-3/Правки_2_для_разработчика.md` §5–§12: Идентификация →
 * Сотрудник → Режим работы → Предыдущая спецоценка → Компенсации → Несчастные случаи →
 * Оборудование и материалы (ОДИН блок, §11) → Служебный транспорт. Полностью контролируемая:
 * любой ввод поднимается через onChange — родитель обновляет строку и автосохраняет ПОЛНЫЙ
 * объект `conditions` (см. `pages/order-sout-workplace.tsx`, §13/п.6.13 tasks.md — критично для
 * серверной `syncSoutConditionsToStructure`, которая иначе затрёт адрес/график структуры
 * пустыми значениями частичного диффа). Невалидные обязательные поля подсвечиваются оранжевым
 * (design D6 — не красным). В read-only (`readOnly=true`, отправленный заказ) — только просмотр.
 *
 * Убрано из формы (Resolved заказчика, design D8): переключатель «Тип рабочего места»
 * (Действующее/Вакансия) — «место без сотрудника» вычисляется по отсутствию назначенного
 * сотрудника (§6/§16); чекбокс «Прошлая СОУТ проводилась» — предыдущая СОУТ всегда
 * необязательна (§8/§16); отдельное поле «Иной график» — график только из фиксированного
 * списка §7 (значение «Иной» — как элемент списка, без сопроводительного текстового поля).
 * Старые значения (`rmType`/`hadPreviousSout`/`scheduleOther`) остаются в снимке нетронутыми —
 * просто не редактируются этой формой (§15).
 *
 * centrika-docs!93: из блока «Сотрудник» убрано поле «Дата рождения» (в профиле сотрудника она
 * остаётся и этой формой НЕ изменяется), а чекбокс «Сотрудник младше 18 лет» стал ручным:
 * для нового сотрудника не установлен, для сотрудника из структуры показывает сохранённый в
 * профиле признак, при сохранении формы записывается в профиль (сервер, `syncSoutPersonToStructure`
 * / `handleAddManualSoutLine`).
 *
 * `manual` (по умолчанию `false`) — режим страницы «Добавить вручную» (§14, Волна 7): при
 * `true` подразделение/должность/ФИО становятся редактируемыми (селект существующих подразделений
 * с пунктом «добавить новое»/справочник должностей/ввод нового сотрудника) вместо read-only/списка
 * сотрудников структуры (§5/§6 «при ручном создании»). Подразделение — селект из `departmentOptions`
 * (существующие подразделения компании) плюс пункт «+ Добавить новое подразделение», раскрывающий
 * свободный ввод названия (p3-critical-fixes №4, уточнение РП); в запрос уходит `departmentName`
 * строкой в любом случае — бэкенд неизменен (`resolveDepartment` find-or-create по названию).
 * Подключена в `pages/order-sout-workplace-new.tsx` — та же форма ведёт ЛОКАЛЬНЫЙ черновик
 * (без autosave, строки на сервере ещё нет) и её единственная кнопка «Готово — к списку
 * рабочих мест» (см. ниже) вызывает `onBack`, который на этой странице означает не переход, а
 * атомарное создание места(+сотрудника)+строки (`soutApi.addManualLine`) с последующим
 * переходом при успехе. `serverFieldErrors`/`doneDisabled` — поддержка этого сценария (422 у
 * дубля СНИЛС/пары место+должность рядом с полем; блокировка кнопки на время запроса — защита
 * от двойного клика, §14 критерий «повторные/одновременные попытки… не более одного»).
 */
import { useEffect, useState } from 'react'
import { Car, Plus, Trash2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import type {
  Employee,
  SoutAccident,
  SoutConditions,
  SoutLine,
  SoutPerson,
  SoutTransport,
} from '@/lib/api'
import {
  DEFAULT_ACCIDENT_SEVERITY,
  DEFAULT_SCHEDULE,
  SOUT_ACCIDENTS_MAX,
  computeLineMissing,
  derivePreviousSout,
  emptyAccident,
  emptyTransport,
  hasSnilsOrderConflict,
  isPersonPartiallyFilled,
  isValidAccident,
  isValidPlate,
  isValidTransport,
  PLATE_ERROR,
  resolveScheduleDisplay,
} from '@/lib/sout'
import {
  ACCIDENT_SEVERITIES,
  ACCIDENT_SEVERITY_LABELS,
  PREV_CLASSES,
  SCHEDULES,
  SCHEDULE_OPTION_LABELS,
} from '@/lib/sout-dictionaries'
import {
  formatWorkHours,
  isValidWorkHours,
  parseWorkHours,
  WORK_HOURS_ERROR,
  WORK_HOURS_MAX,
} from '@/lib/work-hours'
import { PositionSelect } from '@/company/position-select'
import { EmployeeSelect } from '@/company/employee-select'
import {
  SOUT_ACCIDENTS_EMPTY_TEXT,
  SOUT_ACCIDENT_DESCRIPTION_PLACEHOLDER,
  SOUT_ACTUAL_ADDRESS_PLACEHOLDER,
  SOUT_COMPENSATION_LABELS,
  SOUT_COMPENSATIONS_HINT,
  SOUT_EQUIPMENT_MATERIALS_HINT,
  SOUT_EQUIPMENT_PLACEHOLDER,
  SOUT_MATERIALS_PLACEHOLDER,
  SOUT_HINTS,
  SOUT_SNILS_ORDER_CONFLICT,
  SOUT_TRANSPORT_EMPTY_TEXT,
  SOUT_TRANSPORT_INFO,
  SOUT_TRANSPORT_TITLE,
  SOUT_WARNING,
  SOUT_WORKPLACE_FIELD_LABELS,
} from './sout-copy'
import { LabeledCheckbox } from './labeled-checkbox'
import { Card } from '@/design-system/components/layout/card'
import { Grid } from '@/design-system/components/layout/grid'
import { Button } from '@/design-system/components/primitives/button'
import { Field } from '@/design-system/components/primitives/field'
import { Input } from '@/design-system/components/primitives/input'
import { DateInput } from '@/design-system/components/primitives/date-input'
import { SnilsInput } from '@/design-system/components/primitives/snils-input'
import { Alert } from '@/design-system/components/feedback/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/design-system/components/primitives/select'
import { normalizePersonName } from '@/lib/name-case'

const TEXTAREA_CLASS =
  'w-full rounded-8 border border-line-strong bg-white px-3 py-2 text-14 text-txt outline-none transition-colors placeholder:text-txt-tertiary hover:border-line-strong focus-visible:ring-2 focus-visible:ring-accent disabled:cursor-not-allowed disabled:bg-subtle disabled:opacity-50'

/** Классы подсветки неполной добавленной записи (НС/транспорт) — оранжевая, НЕ красная (D6). */
const INVALID_RECORD_CLASS = 'border-amber bg-amber-bg'
const VALID_RECORD_CLASS = 'border-line bg-white'

const NO_PERSON = 'none'

/**
 * Пункт «добавить новое подразделение» в селекте подразделения manual-режима (p3-critical-fixes
 * №4, уточнение РП): значение-сентинел, отличимое от любого реального названия подразделения —
 * при его выборе селект скрывается и показывается текстовый ввод названия нового подразделения.
 */
const ADD_NEW_DEPARTMENT = '__add_new_department__'

/** Значение поля-числа: пустая строка при <= 0 (транспорт/несчастные случаи — без авто-дефолта). */
function numValue(n: number): string {
  return n > 0 ? String(n) : ''
}

/** Целое из строки ввода (год выпуска транспорта) — дробные усечены, некорректный ввод → 0. */
function intValue(raw: string): number {
  const n = Math.trunc(Number(raw))
  return Number.isFinite(n) ? n : 0
}

/**
 * Черновик поля часов режима работы (fractional-work-hours): строка ввода живёт в форме, а в
 * `conditions` (и в автосохранение) уходит только допустимое число — промежуточный ввод «7,»
 * при наборе «7,5» не стирается. Пустое поле → 0 («не заполнено», статус «дозаполнить», как и
 * раньше); недопустимый ввод (три знака после запятой, буквы, больше 9999,99) в `conditions` не
 * уходит, под полем — ошибка, а при уходе с поля строка возвращается к сохранённому значению —
 * поле не показывает то, чего нет в данных. Допустимый ввод при уходе с поля переписывается в
 * показ с запятой («37.5» → «37,5»). Внешняя смена значения (дефолт 8/40 при открытии,
 * другая строка) переписывает черновик, если он с ней не совпадает.
 */
function useWorkHoursDraft(value: number, onValue: (n: number) => void) {
  const [draft, setDraft] = useState(() => formatWorkHours(value))
  useEffect(() => {
    setDraft((d) => ((parseWorkHours(d) ?? 0) === value ? d : formatWorkHours(value)))
  }, [value])
  const parsed = parseWorkHours(draft)
  const invalid = parsed !== null && (Number.isNaN(parsed) || parsed > WORK_HOURS_MAX)
  return {
    draft,
    invalid,
    onChange(raw: string) {
      setDraft(raw)
      const n = parseWorkHours(raw)
      if (n === null) onValue(0)
      else if (n === 0 || isValidWorkHours(n)) onValue(n)
    },
    onBlur() {
      if (invalid || isValidWorkHours(parsed)) setDraft(formatWorkHours(value))
    },
  }
}

function emptyPerson(): SoutPerson {
  return { fullName: '', snils: '', gender: '', birthDate: '', isMinor: false, isDisabled: false }
}

export interface WorkplaceFormProps {
  line: SoutLine
  readOnly: boolean
  employees: Employee[]
  onChange: (next: SoutLine) => void
  /** «Готово — к списку рабочих мест» (§13) — тот же переход, что и «К списку рабочих мест»
   *  в шапке страницы (шапка рендерится страницей-контейнером, p3-sout-workplaces §4). */
  onBack: () => void
  /** Ручной режим (§5/§6/§14) — задел для Волны 7, см. комментарий модуля. Дефолт `false`. */
  manual?: boolean
  /**
   * Существующие подразделения структуры компании — источник опций селекта «Структурное
   * подразделение» в manual-режиме (p3-critical-fixes №4, уточнение РП: выбор из текущих
   * подразделений + пункт «добавить новое»). Загружается страницей-контейнером
   * (`order-sout-workplace-new.tsx`, `companyApi.listDepartments`) и передаётся сюда. Пустой
   * список (нет подразделений ИЛИ ошибка загрузки — форма не блокируется) → сразу свободный ввод.
   * В non-manual режиме не используется (подразделение read-only). Дефолт `[]`.
   */
  departmentOptions?: string[]
  /**
   * Ошибки полей с сервера (422 `details`, ключи БЕЗ префикса `person.`) — страница «Добавить
   * вручную» (§14, D7, Волна 7) маппит туда дубль СНИЛС (`snils`)/дубль пары место+должность
   * (`departmentName`/`position`)/валидацию частично введённого сотрудника
   * (`fullName`/`birthDate`/`gender`). Приоритетнее локальной подсветки «дозаполнить» у того же
   * поля; данные формы не сбрасываются — ошибка только подсвечивает поле. Дефолт `{}`.
   */
  serverFieldErrors?: Record<string, string>
  /**
   * Дизейблит кнопку «Готово — к списку рабочих мест» (защита от двойного клика на in-flight
   * запросе — §13/§14). Дефолт `false` (текущее поведение страницы карточки РМ не меняется).
   */
  doneDisabled?: boolean
  /**
   * Рабочие места ТЕКУЩЕГО заказа СОУТ — источник проверки совпадений СНИЛС прямо в форме
   * (centrika-docs!94). Страница-контейнер передаёт уже загруженные строки анкеты
   * (`anketa.lines`) — отдельного запроса проверка не делает; редактируемая строка исключается
   * по `id`. Дефолт `[]` (проверять не с чем — сообщение не показывается).
   */
  orderLines?: SoutLine[]
}

export function WorkplaceForm({
  line,
  readOnly,
  employees,
  onChange,
  onBack,
  manual = false,
  departmentOptions = [],
  serverFieldErrors = {},
  doneDisabled = false,
  orderLines = [],
}: WorkplaceFormProps) {
  const c = line.conditions
  const p = line.person
  // p3-critical-fixes (№4, уточнение РП): подразделение в manual-режиме — селект из существующих
  // подразделений компании + пункт «добавить новое». `departmentAddNew` — локальный UI-стейт
  // «пользователь вводит новое подразделение вручную» (селект скрыт, показан текстовый ввод).
  // Свободный ввод также используется, когда у компании нет подразделений (`departmentOptions`
  // пуст) — селект без реальных опций не имеет смысла. Значение уходит в `conditions.departmentName`
  // (строка) в обоих под-режимах — бэкенд неизменен (`resolveDepartment` find-or-create).
  const [departmentAddNew, setDepartmentAddNew] = useState(false)
  const departmentFreeInput = departmentOptions.length === 0 || departmentAddNew
  // Блок «Сотрудник» необязателен, пока не заполнено ФИО (centrika-docs, MR !13) — правило
  // едино для ручного создания и правки существующей строки (зеркало сервера).
  const missing = computeLineMissing(line)
  const has = (field: string) => missing.includes(field) && !readOnly
  // Звёздочка у подписи (common.required-fields) — ровно по правилам `computeLineMissing`:
  // в просмотре заполнять нечего, поля структуры (не-manual) подставлены и недоступны, а блок
  // «Сотрудник» обязателен только когда сотрудник назначен или введено ФИО (MR !13).
  const req = !readOnly
  const reqManual = req && manual
  const reqPerson = req && (Boolean(line.personId) || Boolean(p.fullName.trim()))
  // centrika-docs!94: совпадение СНИЛС с ДРУГИМ сотрудником этого же заказа. Считается на каждом
  // рендере по уже загруженным строкам заказа (`orderLines`) — сразу после ввода/вставки полного
  // корректного СНИЛС и при каждой правке СНИЛС/ФИО, без сохранения формы и без ухода из поля.
  // В просмотре (отправленный заказ) не показывается — правок там нет.
  const snilsOrderConflict = !readOnly && hasSnilsOrderConflict(line, orderLines)
  const scheduleDisplay = resolveScheduleDisplay(c)
  // MR #69: поле «Комментарий» к режиму работы показывается только при графике «иной», обязательно,
  // пока показано, и при другом графике скрыто и не участвует в валидации.
  const scheduleIsOther = scheduleDisplay.schedule === 'Иной'
  const scheduleCommentMissing = !readOnly && scheduleIsOther && !scheduleDisplay.scheduleOther.trim()
  // §8: `previousSout` — ТОЛЬКО метаданные источника (editable/source), НЕ отображаемые значения.
  // Opus-ревью Волны 6 (HIGH): раньше Select/Input §8 показывали `previousSout.class/rmNumber` —
  // родитель (`order-sout-workplace.tsx`) не пересчитывает `line.previousSout` на каждый onChange
  // (сервер резолвит его ЛИШЬ при создании строки/смене места, §13 — «фотография»), поэтому
  // контролируемый инпут откатывался на СТАРОЕ значение на следующем рендере — правка визуально
  // «зависала» (хотя реально уходила в `conditions.previousClass/rmNumber` и сохранялась). Теперь
  // ОТОБРАЖАЕМЫЕ значения полей — всегда живые `c.previousClass`/`c.rmNumber`.
  const previousSout = line.previousSout ?? derivePreviousSout(c)
  const previousReadOnly = readOnly || !previousSout.editable

  function patchConditions(patch: Partial<SoutConditions>) {
    onChange({ ...line, conditions: { ...c, ...patch } })
  }
  function patchPerson(patch: Partial<SoutPerson>) {
    onChange({ ...line, person: { ...p, ...patch } })
  }
  const shiftHours = useWorkHoursDraft(c.shiftHours, (n) => patchConditions({ shiftHours: n }))
  const weekHours = useWorkHoursDraft(c.weekHours, (n) => patchConditions({ weekHours: n }))

  // §7 «для нового места автоматически установлены значения 8/40/5-2»: дефолты режима работы
  // проставляются ОДИН РАЗ при открытии конкретной строки — в deps сознательно НЕТ
  // `c.shiftHours`/`c.weekHours`/`c.schedule`: реагируй эффект на них, живой ввод (транзитный 0
  // при очистке поля для набора нового числа) немедленно возвращал бы дефолт, не позволяя
  // набрать значение.
  //
  // centrika-docs!93: признак «Сотрудник младше 18 лет» больше НЕ вычисляется — он ручной
  // (чекбокс ниже) и приходит либо пустым для нового сотрудника, либо сохранённым из профиля
  // сотрудника структуры. Прежний авто-пересчёт по дате рождения из этого эффекта убран.
  useEffect(() => {
    if (readOnly) return
    const conditionsPatch: Partial<SoutConditions> = {}
    if (!(c.shiftHours > 0)) conditionsPatch.shiftHours = 8
    if (!(c.weekHours > 0)) conditionsPatch.weekHours = 40
    if (!c.schedule.trim()) conditionsPatch.schedule = DEFAULT_SCHEDULE
    if (Object.keys(conditionsPatch).length > 0) {
      onChange({ ...line, conditions: { ...c, ...conditionsPatch } })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [line.id, readOnly])

  function selectEmployee(id: string) {
    if (id === NO_PERSON) {
      onChange({ ...line, personId: null, person: emptyPerson() })
      return
    }
    const e = employees.find((x) => x.id === id)
    if (!e) return
    onChange({
      ...line,
      personId: e.id,
      person: {
        fullName: e.fullName,
        snils: e.snils,
        gender: e.gender,
        birthDate: e.birthDate,
        isMinor: e.isMinor,
        isDisabled: e.hasDisability,
      },
    })
  }

  /**
   * Свободный ввод ФИО в поле «Сотрудник» (centrika-docs!100): введённое имя — это НОВЫЙ
   * сотрудник, поэтому строка отвязывается от сотрудника структуры. Если сотрудник до этого был
   * выбран, остальные его поля (СНИЛС, пол, дата рождения, признаки) сбрасываются: они
   * принадлежали другому человеку, и оставлять их в блоке нового — значит завести его с чужим
   * СНИЛС. Сотрудник создаётся в структуре сервером при сохранении строки, не здесь.
   */
  function setNewPersonName(fullName: string) {
    if (line.personId) {
      onChange({ ...line, personId: null, person: { ...emptyPerson(), fullName } })
      return
    }
    patchPerson({ fullName })
  }

  // --- Мини-список: служебный транспорт (§12) ---
  function addTransport() {
    patchConditions({ transport: [...c.transport, emptyTransport()] })
  }
  function updateTransport(i: number, patch: Partial<SoutTransport>) {
    patchConditions({ transport: c.transport.map((t, j) => (j === i ? { ...t, ...patch } : t)) })
  }
  function removeTransport(i: number) {
    patchConditions({ transport: c.transport.filter((_, j) => j !== i) })
  }

  // --- Мини-список: несчастные случаи (§10) ---
  function addAccident() {
    patchConditions({ accidents: [...c.accidents, emptyAccident()] })
  }
  function updateAccident(i: number, patch: Partial<SoutAccident>) {
    patchConditions({ accidents: c.accidents.map((a, j) => (j === i ? { ...a, ...patch } : a)) })
  }
  function removeAccident(i: number) {
    patchConditions({ accidents: c.accidents.filter((_, j) => j !== i) })
  }

  const maxYear = new Date().getFullYear() + 1

  return (
    <div className="flex flex-col gap-4">
      {/* Шапка страницы (кнопка «К списку рабочих мест», заголовок «Рабочее место — <должность>»,
          подпись) рендерится страницей-контейнером (p3-sout-workplaces §4, `order-sout-workplace.tsx`),
          не этой формой. */}
      {!readOnly ? (
        <Alert variant="info" title="Внимание">
          {SOUT_WARNING}
        </Alert>
      ) : null}

      {/* §5 Идентификация */}
      <Card header="Идентификация">
        <Grid cols={2}>
          <Field
            label={SOUT_WORKPLACE_FIELD_LABELS.department}
            required={reqManual}
            error={
              serverFieldErrors.departmentName ??
              (manual && has('departmentName') ? 'Укажите подразделение' : undefined)
            }
          >
            {(props) => {
              if (!manual) {
                return <Input {...props} value={c.departmentName} disabled />
              }
              const departmentInvalid =
                has('departmentName') || Boolean(serverFieldErrors.departmentName)
              // Свободный ввод: нет существующих подразделений ИЛИ выбран пункт «добавить новое».
              if (departmentFreeInput) {
                return (
                  <div className="flex flex-col gap-1.5">
                    <Input
                      {...props}
                      value={c.departmentName}
                      invalid={departmentInvalid}
                      disabled={readOnly}
                      onChange={(e) => patchConditions({ departmentName: e.target.value })}
                      placeholder="Например, Цех №1"
                      autoComplete="off"
                    />
                    {/* Возврат к выбору из списка — если у компании есть подразделения и ввод нового
                        открылся по ошибке (или клиент передумал заводить новое). */}
                    {departmentOptions.length > 0 ? (
                      <button
                        type="button"
                        className="self-start text-12 text-accent hover:underline disabled:opacity-50"
                        disabled={readOnly}
                        onClick={() => {
                          setDepartmentAddNew(false)
                          patchConditions({ departmentName: '' })
                        }}
                      >
                        Выбрать из списка
                      </button>
                    ) : null}
                  </div>
                )
              }
              // Селект существующих подразделений + пункт «добавить новое».
              return (
                <Select
                  value={c.departmentName || ''}
                  onValueChange={(v) => {
                    if (v === ADD_NEW_DEPARTMENT) {
                      setDepartmentAddNew(true)
                      patchConditions({ departmentName: '' })
                    } else {
                      setDepartmentAddNew(false)
                      patchConditions({ departmentName: v })
                    }
                  }}
                  disabled={readOnly}
                >
                  <SelectTrigger
                    id={props.id}
                    aria-label={SOUT_WORKPLACE_FIELD_LABELS.department}
                    invalid={departmentInvalid}
                  >
                    <SelectValue placeholder="Выберите подразделение" />
                  </SelectTrigger>
                  <SelectContent>
                    {/* Пункт-действие первым, ниже разделительная черта, ниже подразделения компании
                        (centrika-docs, page.create-sout-order.new-workplace, MR !8). */}
                    <SelectItem value={ADD_NEW_DEPARTMENT}>+ Добавить новое подразделение</SelectItem>
                    <SelectSeparator />
                    {departmentOptions.map((d) => (
                      <SelectItem key={d} value={d}>
                        {d}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )
            }}
          </Field>
          <Field
            label="Фактический адрес рабочего места"
            required={req}
            hint={SOUT_HINTS.actualAddress}
            error={has('actualAddress') ? 'Укажите фактический адрес' : undefined}
          >
            {(props) => (
              <Input
                {...props}
                value={c.actualAddress}
                invalid={has('actualAddress')}
                disabled={readOnly}
                placeholder={SOUT_ACTUAL_ADDRESS_PLACEHOLDER}
                onChange={(e) => patchConditions({ actualAddress: e.target.value })}
              />
            )}
          </Field>
        </Grid>
        <Field
          label={SOUT_WORKPLACE_FIELD_LABELS.position}
          required={reqManual}
          error={
            // Дубль пары «подразделение+должность» (D7) сервер помечает полем `departmentName` —
            // показываем то же сообщение и здесь, у второй половины составного ключа.
            serverFieldErrors.position ??
            serverFieldErrors.departmentName ??
            (manual && has('position') ? 'Укажите должность' : undefined)
          }
        >
          {(props) =>
            manual ? (
              <PositionSelect
                {...props}
                valueName={c.position}
                // centrika-docs!85: «выбор ИЛИ ввод новой» (паритет с формой добавления РМ
                // структуры docs!62) — новая должность заводится сервером при сохранении РМ
                // (find-or-create в транзакции). Храним только текст (снимок условий); id не нужен.
                allowCreate
                invalid={has('position') || Boolean(serverFieldErrors.position || serverFieldErrors.departmentName)}
                disabled={readOnly}
                onChange={(_id, name) => patchConditions({ position: name })}
              />
            ) : (
              <Input {...props} value={c.position} disabled />
            )
          }
        </Field>
      </Card>

      {/* §6 Сотрудник */}
      <Card header="Сотрудник">
        <Grid cols={3}>
          <Field
            label="ФИО"
            required={reqPerson}
            error={serverFieldErrors.fullName ?? (has('fullName') ? 'Проверьте ФИО (3 слова, без инициалов)' : undefined)}
          >
            {(props) =>
              manual ? (
                <Input
                  {...props}
                  value={p.fullName}
                  invalid={has('fullName') || Boolean(serverFieldErrors.fullName)}
                  disabled={readOnly}
                  onChange={(e) => patchPerson({ fullName: e.target.value })}
                  // «Иванов Иван Иванович» — на уходе с поля (names-and-positions-normalization);
                  // без изменения регистра лишний autosave не запускается.
                  onBlur={() => {
                    const normalized = normalizePersonName(p.fullName)
                    if (normalized !== p.fullName) patchPerson({ fullName: normalized })
                  }}
                  spellCheck={false}
                />
              ) : (
                <div className="flex flex-col gap-1.5">
                  {/* Общий combobox одиночного выбора сотрудника с поиском по ФИО (centrika-docs,
                      common.md, MR !22). Опции создания сотрудника у поля нет → разделительной
                      черты перед списком тоже нет; «Не назначен» — опция отсутствия выбора.
                      centrika-docs!100: поле принимает и ФИО НОВОГО сотрудника (`allowCreate`) —
                      в первую очередь для копии рабочего места, где ФИО и СНИЛС обнулены.
                      Связывание с существующим — только явным выбором строки списка. */}
                  <EmployeeSelect
                    id={props.id}
                    aria-label="ФИО"
                    allowCreate
                    value={line.personId ?? NO_PERSON}
                    // ФИО из снимка строки — фолбэк отображения (issue #13): список сотрудников
                    // грузится первой страницей активных (200), поэтому связанного сотрудника в
                    // нём может не оказаться — деактивирован или за пределами страницы. Без
                    // фолбэка поле показывало пустоту с подсказкой «Выберите сотрудника», хотя
                    // ФИО в строке сохранено. Найденный в списке сотрудник всё равно
                    // приоритетнее: его метку показывает сам компонент.
                    valueLabel={p.fullName || undefined}
                    noneOption={{ value: NO_PERSON, label: 'Не назначен' }}
                    options={employees.map((e) => ({
                      value: e.id,
                      label: `${e.fullName} — ${e.position}`,
                      fullName: e.fullName,
                    }))}
                    onSelect={selectEmployee}
                    onFreeText={setNewPersonName}
                    invalid={has('fullName') || Boolean(serverFieldErrors.fullName)}
                    disabled={readOnly}
                  />
                  {/* §15 (Opus-ревью Волны 6, MAJOR): легаси-строка со свободно введённым ФИО без
                      привязки к сотруднику (до p3-sout-workplaces, когда ФИО было текстовым полем
                      даже в режиме «существующая строка») — раньше поле показывало «Не назначен»,
                      и данные иначе были бы не видны. С centrika-docs!100 такое ФИО показывает
                      само поле; строка остаётся для остальных заполненных полей блока. */}
                  {!line.personId && !p.fullName.trim() && isPersonPartiallyFilled(p) ? (
                    <p className="text-12 text-txt-tertiary">Ранее указаны данные сотрудника без ФИО</p>
                  ) : null}
                </div>
              )
            }
          </Field>
          <Field
            label="СНИЛС"
            required={reqPerson}
            error={
              serverFieldErrors.snils ??
              (has('snils')
                ? 'Проверьте СНИЛС (11 цифр)'
                : snilsOrderConflict
                  ? SOUT_SNILS_ORDER_CONFLICT
                  : undefined)
            }
          >
            {(props) => (
              <SnilsInput
                {...props}
                value={p.snils}
                invalid={has('snils') || snilsOrderConflict || Boolean(serverFieldErrors.snils)}
                disabled={readOnly}
                onChange={(snils) => patchPerson({ snils })}
              />
            )}
          </Field>
        </Grid>
        <Grid cols={2}>
          <Field
            label="Пол"
            required={reqPerson}
            error={serverFieldErrors.gender ?? (has('gender') ? 'Укажите пол' : undefined)}
          >
            {(props) => (
              // Пол выбирается парой кнопок-переключателей, отдельного контрола ввода нет.
              // `aria-required` роль `group` не поддерживает, поэтому обязательность здесь
              // остаётся только визуальной — как и было до маркера.
              <div
                id={props.id}
                className="flex gap-2"
                role="group"
                aria-label="Пол"
                aria-describedby={props['aria-describedby']}
              >
                <Button
                  type="button"
                  size="sm"
                  variant={p.gender === 'm' ? 'primary' : 'secondary'}
                  aria-pressed={p.gender === 'm'}
                  disabled={readOnly}
                  onClick={() => patchPerson({ gender: 'm' })}
                >
                  Мужской
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={p.gender === 'f' ? 'primary' : 'secondary'}
                  aria-pressed={p.gender === 'f'}
                  disabled={readOnly}
                  onClick={() => patchPerson({ gender: 'f' })}
                >
                  Женский
                </Button>
              </div>
            )}
          </Field>
          {/* «Есть инвалидность» СКРЫТ из блока (centrika-docs,
              page.create-sout-order.new-workplace, MR !37): в «Особых категориях» остаётся
              только несовершеннолетие. Признак остаётся в данных строки заказа — он
              по-прежнему подставляется из карточки выбранного сотрудника (см. `selectEmployee`)
              и уходит на сервер вместе с остальными полями блока «Сотрудник». */}
          <Field label="Особые категории">
            {() => (
              <div className="flex flex-col gap-2">
                {/* centrika-docs!93: чекбокс ставит и снимает КЛИЕНТ — и при добавлении, и при
                    редактировании РМ. Для нового сотрудника он не установлен; для сотрудника из
                    структуры показывается сохранённый в профиле признак (см. `selectEmployee`).
                    Дата рождения состояние чекбокса не пересчитывает. */}
                <LabeledCheckbox
                  checked={p.isMinor}
                  disabled={readOnly}
                  onCheckedChange={(v) => patchPerson({ isMinor: v })}
                >
                  Сотрудник младше 18 лет
                </LabeledCheckbox>
              </div>
            )}
          </Field>
        </Grid>
      </Card>

      {/* §7 Режим работы */}
      <Card header="Режим работы">
        <Grid cols={3}>
          <Field
            label="Продолжительность смены, часов"
            required={req}
            hint={SOUT_HINTS.shiftHours}
            error={shiftHours.invalid || has('shiftHours') ? WORK_HOURS_ERROR : undefined}
          >
            {(props) => (
              <Input
                {...props}
                inputMode="decimal"
                value={shiftHours.draft}
                invalid={shiftHours.invalid || has('shiftHours')}
                disabled={readOnly}
                onChange={(e) => shiftHours.onChange(e.target.value)}
                onBlur={shiftHours.onBlur}
              />
            )}
          </Field>
          <Field
            label="Часов в неделю"
            required={req}
            hint={SOUT_HINTS.weekHours}
            error={weekHours.invalid || has('weekHours') ? WORK_HOURS_ERROR : undefined}
          >
            {(props) => (
              <Input
                {...props}
                inputMode="decimal"
                value={weekHours.draft}
                invalid={weekHours.invalid || has('weekHours')}
                disabled={readOnly}
                onChange={(e) => weekHours.onChange(e.target.value)}
                onBlur={weekHours.onBlur}
              />
            )}
          </Field>
          <Field label="График работы" required={req}>
            {(props) => (
              <Select
                value={scheduleDisplay.schedule}
                // MR #69: при уходе с графика «иной» комментарий очищается (поле скрывается и не
                // проверяется), при выборе «иной» показывается поле «Комментарий».
                onValueChange={(v) =>
                  patchConditions(v === 'Иной' ? { schedule: v } : { schedule: v, scheduleOther: '' })
                }
                disabled={readOnly}
              >
                <SelectTrigger
                  id={props.id}
                  aria-label="График работы"
                  aria-required={props['aria-required']}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SCHEDULES.map((s) => (
                    <SelectItem key={s} value={s}>
                      {SCHEDULE_OPTION_LABELS[s]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>
        </Grid>
        {/* MR #69: комментарий к режиму работы — только для графика «иной», обязателен пока показан. */}
        {scheduleIsOther ? (
          <Field
            label="Комментарий"
            required={req}
            className="mb-0 mt-4"
            error={scheduleCommentMissing ? 'Укажите комментарий к режиму работы.' : undefined}
          >
            {(props) => (
              <Input
                {...props}
                value={scheduleDisplay.scheduleOther}
                invalid={scheduleCommentMissing}
                disabled={readOnly}
                onChange={(e) => patchConditions({ scheduleOther: e.target.value })}
              />
            )}
          </Field>
        ) : null}
      </Card>

      {/* §8 Предыдущая спецоценка */}
      <Card header="Предыдущая спецоценка">
        <Grid cols={2}>
          <Field label="Класс условий труда по прошлой СОУТ">
            {() => (
              <Select
                value={c.previousClass}
                onValueChange={(v) => patchConditions({ previousClass: v })}
                disabled={previousReadOnly}
              >
                <SelectTrigger aria-label="Класс условий труда по прошлой СОУТ">
                  <SelectValue placeholder="Выберите класс" />
                </SelectTrigger>
                <SelectContent>
                  {PREV_CLASSES.map((cls) => (
                    <SelectItem key={cls} value={cls}>
                      {cls}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>
          <Field label="Номер рабочего места по прошлой СОУТ" hint={SOUT_HINTS.rmNumber}>
            {(props) => (
              <Input
                {...props}
                value={c.rmNumber}
                disabled={previousReadOnly}
                onChange={(e) => patchConditions({ rmNumber: e.target.value })}
              />
            )}
          </Field>
        </Grid>
      </Card>

      {/* §9 Компенсации за вредные условия */}
      <Card header="Компенсации за вредные условия">
        <p className="mb-3 text-13 text-txt-secondary">{SOUT_COMPENSATIONS_HINT}</p>
        <div className="flex flex-col gap-2">
          {(Object.keys(SOUT_COMPENSATION_LABELS) as Array<keyof typeof SOUT_COMPENSATION_LABELS>).map((key) => (
            <LabeledCheckbox
              key={key}
              checked={c.compensations[key]}
              disabled={readOnly}
              onCheckedChange={(v) => patchConditions({ compensations: { ...c.compensations, [key]: v } })}
            >
              {SOUT_COMPENSATION_LABELS[key]}
            </LabeledCheckbox>
          ))}
        </div>
      </Card>

      {/* §10 Несчастные случаи и профзаболевания за 5 лет */}
      <Card header="Несчастные случаи и профзаболевания за 5 лет">
        {c.accidents.length === 0 ? (
          <p className="py-2 text-txt-secondary">{SOUT_ACCIDENTS_EMPTY_TEXT}</p>
        ) : (
          c.accidents.map((a, i) => {
            const invalid = !readOnly && !isValidAccident(a)
            return (
              <div
                key={i}
                className={cn('mb-3 rounded-10 border p-3', invalid ? INVALID_RECORD_CLASS : VALID_RECORD_CLASS)}
              >
                <Grid cols={3}>
                  <Field label="Дата случая" required={req} className="my-0">
                    {(props) => (
                      <DateInput
                        {...props}
                        value={a.date}
                        invalid={invalid && !a.date.trim()}
                        disabled={readOnly}
                        onChange={(date) => updateAccident(i, { date })}
                      />
                    )}
                  </Field>
                  <Field label="Тяжесть" required={req} className="my-0">
                    {(props) => (
                      <Select
                        value={a.severity || DEFAULT_ACCIDENT_SEVERITY}
                        onValueChange={(v) => updateAccident(i, { severity: v })}
                        disabled={readOnly}
                      >
                        <SelectTrigger
                          id={props.id}
                          aria-label="Тяжесть несчастного случая"
                          aria-required={props['aria-required']}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ACCIDENT_SEVERITIES.map((s) => (
                            <SelectItem key={s} value={s}>
                              {ACCIDENT_SEVERITY_LABELS[s]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </Field>
                  <Field label="Кратко, что произошло" required={req} className="my-0">
                    {(props) => (
                      <Input
                        {...props}
                        value={a.description}
                        invalid={invalid && !a.description.trim()}
                        disabled={readOnly}
                        placeholder={SOUT_ACCIDENT_DESCRIPTION_PLACEHOLDER}
                        onChange={(e) => updateAccident(i, { description: e.target.value })}
                      />
                    )}
                  </Field>
                </Grid>
                {!readOnly ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    className="mt-3"
                    onClick={() => removeAccident(i)}
                    leftIcon={<Trash2 className="size-4 stroke-current" aria-hidden />}
                  >
                    Удалить случай
                  </Button>
                ) : null}
              </div>
            )
          })
        )}
        {!readOnly ? (
          <Button
            variant="secondary"
            size="sm"
            onClick={addAccident}
            disabled={c.accidents.length >= SOUT_ACCIDENTS_MAX}
            leftIcon={<Plus className="size-4 stroke-current" aria-hidden />}
          >
            Добавить случай
          </Button>
        ) : null}
      </Card>

      {/* §11 Оборудование и материалы — ОДИН визуальный блок (текст ТЗ дословно) */}
      <Card header="Оборудование и материалы">
        <Field
          label="Оборудование и инструменты на рабочем месте"
          required={req}
          hint={SOUT_HINTS.equipment}
          error={has('equipment') ? 'Укажите оборудование или «Нет»' : undefined}
          className="my-0"
        >
          {(props) => (
            <textarea
              {...props}
              value={c.equipment}
              disabled={readOnly}
              rows={2}
              className={TEXTAREA_CLASS}
              placeholder={SOUT_EQUIPMENT_PLACEHOLDER}
              aria-invalid={has('equipment') || undefined}
              onChange={(e) => patchConditions({ equipment: e.target.value })}
            />
          )}
        </Field>
        <Field
          label="Материалы и сырьё"
          required={req}
          hint={SOUT_HINTS.materials}
          error={has('materials') ? 'Укажите материалы или «Нет»' : undefined}
          className="mb-0 mt-4"
        >
          {(props) => (
            <textarea
              {...props}
              value={c.materials}
              disabled={readOnly}
              rows={2}
              className={TEXTAREA_CLASS}
              placeholder={SOUT_MATERIALS_PLACEHOLDER}
              aria-invalid={has('materials') || undefined}
              onChange={(e) => patchConditions({ materials: e.target.value })}
            />
          )}
        </Field>
        <p className="mt-3 text-12 text-txt-tertiary">{SOUT_EQUIPMENT_MATERIALS_HINT}</p>
      </Card>

      {/* §12 Служебный транспорт (centrika-docs, MR !15: заголовок + инфо-блок + текст пустого) */}
      <Card header={SOUT_TRANSPORT_TITLE}>
        <Alert
          variant="info"
          className="mb-3"
          icon={<Car className="size-5 stroke-current" aria-hidden data-testid="transport-info-icon" />}
        >
          {SOUT_TRANSPORT_INFO}
        </Alert>
        {c.transport.length === 0 ? (
          <p className="py-2 text-txt-secondary">{SOUT_TRANSPORT_EMPTY_TEXT}</p>
        ) : (
          c.transport.map((t, i) => {
            // Госномер обязателен, формат не проверяется (тракторы, прицепы и т. п.).
            const plateInvalid = !isValidPlate(t.plate)
            const invalid = !readOnly && (!isValidTransport(t) || plateInvalid)
            return (
              <div
                key={i}
                className={cn('mb-3 rounded-10 border p-3', invalid ? INVALID_RECORD_CLASS : VALID_RECORD_CLASS)}
              >
                <Grid cols={3}>
                  <Field label="Марка и модель" required={req} className="my-0">
                    {(props) => (
                      <Input
                        {...props}
                        value={t.brand}
                        invalid={invalid && !t.brand.trim()}
                        disabled={readOnly}
                        onChange={(e) => updateTransport(i, { brand: e.target.value })}
                      />
                    )}
                  </Field>
                  <Field
                    label="Гос. номер"
                    required={req}
                    className="my-0"
                    error={invalid && plateInvalid ? PLATE_ERROR : undefined}
                  >
                    {(props) => (
                      <Input
                        {...props}
                        value={t.plate}
                        invalid={invalid && plateInvalid}
                        disabled={readOnly}
                        placeholder={SOUT_HINTS.transportPlate}
                        onChange={(e) => updateTransport(i, { plate: e.target.value })}
                      />
                    )}
                  </Field>
                  <Field
                    label="Год выпуска"
                    required={req}
                    hint={SOUT_HINTS.transportYear}
                    className="my-0"
                  >
                    {(props) => (
                      <Input
                        {...props}
                        type="number"
                        min={1900}
                        max={maxYear}
                        value={numValue(t.year)}
                        invalid={invalid && !(t.year >= 1900 && t.year <= maxYear)}
                        disabled={readOnly}
                        onChange={(e) => updateTransport(i, { year: intValue(e.target.value) })}
                      />
                    )}
                  </Field>
                </Grid>
                <Grid cols={2} className="mt-3">
                  <Field label="Масса с грузом, кг" required={req} className="my-0">
                    {(props) => (
                      <Input
                        {...props}
                        type="number"
                        min={1}
                        value={numValue(t.massLaden)}
                        invalid={invalid && !(t.massLaden > 0)}
                        disabled={readOnly}
                        onChange={(e) => updateTransport(i, { massLaden: Math.max(0, Number(e.target.value) || 0) })}
                      />
                    )}
                  </Field>
                  <Field label="Масса без груза, кг" required={req} className="my-0">
                    {(props) => (
                      <Input
                        {...props}
                        type="number"
                        min={1}
                        value={numValue(t.massUnladen)}
                        invalid={invalid && !(t.massUnladen > 0)}
                        disabled={readOnly}
                        onChange={(e) =>
                          updateTransport(i, { massUnladen: Math.max(0, Number(e.target.value) || 0) })
                        }
                      />
                    )}
                  </Field>
                </Grid>
                {!readOnly ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    className="mt-3"
                    onClick={() => removeTransport(i)}
                    leftIcon={<Trash2 className="size-4 stroke-current" aria-hidden />}
                  >
                    Удалить транспорт
                  </Button>
                ) : null}
              </div>
            )
          })
        )}
        {!readOnly ? (
          <Button
            variant="secondary"
            size="sm"
            onClick={addTransport}
            leftIcon={<Plus className="size-4 stroke-current" aria-hidden />}
          >
            Добавить транспортное средство
          </Button>
        ) : null}
      </Card>

      {/* §13 Сохранение и возврат */}
      <div>
        <Button variant="secondary" onClick={onBack} disabled={doneDisabled}>
          Готово — к списку рабочих мест
        </Button>
      </div>
    </div>
  )
}
